/**
 * FleetONE Report Data Contract v2
 * 
 * Tipos para relatório consolidado com detalhamento por veículo.
 */

export type Money = number;

export type FleetVehicleCostCategory =
  | "fuel"
  | "maintenance"
  | "tires"
  | "expenses"
  | "documents"
  | "trips";

export type VehicleCostBreakdown = {
  fuel: Money;
  maintenance: Money;
  tires: Money;
  expenses: Money;
  documents: Money;
  trips: Money;
};

export type VehicleCostSummary = {
  totalCost: Money;
  costPerKm: Money;
  km: number;
  breakdown: VehicleCostBreakdown;
};

export type VehicleSummary = {
  vehicle_id: string;
  vehicle_label: string;
  status?: string | null;
  alertsCount?: number | null;
  summary: VehicleCostSummary;
};

export type FuelItem = {
  id: string;
  occurredAt: string;
  station?: string | null;
  liters?: number | null;
  priceUnit?: Money | null;
  totalValue?: Money | null;
  odometerKm?: number | null;
  notes?: string | null;
};

export type MaintenanceItem = {
  id: string;
  planId?: string | null;
  orderNumber: string;
  planName?: string | null;
  executedAt: string;
  description?: string | null;
  supplier?: string | null;
  type?: "preventive" | "corrective" | "predictive" | "other" | string;
  status?: string | null;
  items?: Array<{
    name: string;
    qty?: number | null;
    unitValue?: Money | null;
    totalValue?: Money | null;
  }>;
  totalCost: Money;
  notes?: string | null;
};

export type TireItem = {
  id: string;
  occurredAt: string;
  supplier?: string | null;
  tireModel?: string | null;
  description?: string | null;
  qty?: number | null;
  totalCost: Money;
  notes?: string | null;
};

export type ExpenseItem = {
  id: string;
  occurredAt: string;
  category: string;
  description?: string | null;
  supplier?: string | null;
  totalCost: Money;
  status?: string | null;
  notes?: string | null;
};

export type DocumentItem = {
  id: string;
  name: string;
  type: string;
  issuedAt?: string | null;
  expiryDate?: string | null;
  paidAt?: string | null;
  totalCost?: Money | null;
  status?: "valid" | "expired" | "warning" | string | null;
};

export type TripItem = {
  id: string;
  startAt: string;
  endAt?: string | null;
  distanceKm: number;
  driverName?: string | null;
  origin?: string | null;
  destination?: string | null;
  status?: string | null;
  notes?: string | null;
};

export type MaintPlanItem = {
  id: string;
  name: string;
  type: string;
  status: string;
  approvalStatus?: string | null;
  approvedAt?: string | null;
  category?: string | null;
  periodicityKm?: number | null;
  periodicityDays?: number | null;
  nextExecutionKm?: number | null;
  nextExecutionDate?: string | null;
};

export type VehicleDetailData = VehicleSummary & {
  fuel: FuelItem[];
  maintenance: MaintenanceItem[];
  maintPlans: MaintPlanItem[];
  tires: TireItem[];
  expenses: ExpenseItem[];
  documents: DocumentItem[];
  trips: TripItem[];
};

export type FleetReportSummaryV2 = {
  header: {
    companyName: string;
    periodLabel: string;
    executionId?: string;
  };
  page1: any;
  byVehicle: { rows: VehicleSummary[] };
  byDriver?: { rows: any[] };
};

export type VehicleDetailTruncation = Partial<Record<FleetVehicleCostCategory, boolean>>;

export function formatWorkOrderNumber(id: string): string {
  const s = (id ?? "").replace(/[^a-zA-Z0-9]/g, "").toUpperCase();
  return `OS-${s.slice(0, 6).padEnd(6, "0")}`;
}
